// The draggable divider between the page panel and the conversation panel (desktop only;
// below 45rem the handle is hidden by CSS). It sits between the two panels inside
// `.planBody` and sets the conversation panel's width through the `--plan-main-width`
// custom property on that parent. No stored share means the stylesheet's default split.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';

const STORAGE_KEY = 'mor.planSplit';
const PAGE_MIN_REM = 26;
const MAIN_MIN_REM = 20;
const STEP = 0.02;

function remPx(): number {
  return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}

// The stored quantity is the page panel's share of the space the two panels share, as a
// fraction strictly between 0 and 1. Nothing else (px, conversation width) is ever stored.
function readStored(): number | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    const value = raw === null || raw === undefined ? NaN : Number(raw);
    return Number.isFinite(value) && value > 0 && value < 1 ? value : null;
  } catch {
    return null;
  }
}

function writeStored(value: number): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, String(value));
  } catch {
    // The split lives for this page load only; not worth failing over.
  }
}

export function SplitHandle(): JSX.Element {
  const handleRef = useRef<HTMLDivElement>(null);
  // The page panel's share of the shared space, or null for the stylesheet's default split.
  const shareRef = useRef<number | null>(readStored());
  // Measured percent, for the aria values.
  const [measured, setMeasured] = useState({ now: 67, min: 0, max: 100 });

  // The shared space runs from the page panel's left edge to the conversation panel's right
  // edge, so the rail and handle are excluded and the current split does not matter.
  const panels = useCallback((): {
    parent: HTMLElement;
    left: number;
    total: number;
  } | null => {
    const handle = handleRef.current;
    const parent = handle?.parentElement;
    const page = handle?.previousElementSibling as HTMLElement | null;
    const main = handle?.nextElementSibling as HTMLElement | null;
    if (!parent || !page || !main || page.offsetWidth === 0) {
      return null;
    }
    const left = page.getBoundingClientRect().left;
    return { parent, left, total: main.getBoundingClientRect().right - left };
  }, []);

  const clamp = useCallback((value: number, total: number): number => {
    const rem = remPx();
    const min = (PAGE_MIN_REM * rem) / total;
    const max = Math.max(min, 1 - (MAIN_MIN_REM * rem) / total);
    return Math.min(max, Math.max(min, value));
  }, []);

  // Apply the share, clamped to the room there is now, and refresh the aria numbers.
  const apply = useCallback((): void => {
    const found = panels();
    if (!found) {
      return;
    }
    const { parent, total } = found;
    const current = shareRef.current;
    if (current === null) {
      parent.style.removeProperty('--plan-main-width');
    } else {
      parent.style.setProperty(
        '--plan-main-width',
        `${(1 - clamp(current, total)) * total}px`,
      );
    }
    const rem = remPx();
    setMeasured({
      now: Math.round(clamp(current ?? 2 / 3, total) * 100),
      min: Math.ceil(((PAGE_MIN_REM * rem) / total) * 100),
      max: Math.floor((1 - (MAIN_MIN_REM * rem) / total) * 100),
    });
  }, [panels, clamp]);

  const update = useCallback(
    (next: number): void => {
      shareRef.current = next;
      apply();
    },
    [apply],
  );

  // Apply on mount and whenever the body changes size, which also covers a first layout
  // that had not finished when the handle mounted.
  useEffect(() => {
    apply();
    const parent = handleRef.current?.parentElement;
    const observer =
      typeof ResizeObserver === 'undefined' || !parent ? null : new ResizeObserver(apply);
    if (parent) {
      observer?.observe(parent);
    }
    window.addEventListener('resize', apply);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', apply);
    };
  }, [apply]);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0 || !panels()) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.dataset['dragging'] = 'true';
    document.body.classList.add('is-resizing');
    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.currentTarget.dataset['dragging'] !== 'true') {
      return;
    }
    const found = panels();
    if (!found) {
      return;
    }
    // The page panel runs from its left edge to the pointer.
    update(clamp((event.clientX - found.left) / found.total, found.total));
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.currentTarget.dataset['dragging'] !== 'true') {
      return;
    }
    delete event.currentTarget.dataset['dragging'];
    document.body.classList.remove('is-resizing');
    if (shareRef.current !== null) {
      writeStored(shareRef.current);
    }
  };

  useEffect(() => () => document.body.classList.remove('is-resizing'), []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const found = panels();
    if (!found || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
      return;
    }
    event.preventDefault();
    const current = shareRef.current ?? clamp(2 / 3, found.total);
    const next = clamp(current + (event.key === 'ArrowLeft' ? -STEP : STEP), found.total);
    update(next);
    writeStored(next);
  };

  return (
    <div
      ref={handleRef}
      className="splitHandle"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize conversation panel"
      aria-valuenow={measured.now}
      aria-valuemin={measured.min}
      aria-valuemax={measured.max}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={onKeyDown}
    />
  );
}
